import { useCallback, useEffect, useRef, useState } from 'react';
import { Paper, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getEventRequests, updateRequest } from '../../../api/requests';
import { connectAdminNotifications } from '../../../api/realtime';
import type { RequestItem as RequestItemType } from '../../../api/types';
import { EventSelector } from '../../../components/EventSelector';
import { RequestList } from '../../../components/RequestList';
import { LoadingScreen } from '../../../components/ui/LoadingScreen';
import { useAdminData } from '../../../hooks/useAdminData';
import { logout } from '../../../store/admin.slice';
import { logout as logoutRequest } from '../../../api/auth';
import { ADMIN_STORAGE_KEY } from '../../../api/client';
import { useDispatch } from 'react-redux';

export function RequestSection() {
    const admin = useAdminData();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [eventId, setEventId] = useState<number | ''>('');
    const [requests, setRequests] = useState<RequestItemType[]>([]);
    const [loading, setLoading] = useState(false);
    const [reload, setReload] = useState(false);

    const handleUnauthorized = useCallback(() => {
        localStorage.removeItem(ADMIN_STORAGE_KEY);
        dispatch(logout());
        // M4: azzera anche il cookie di autenticazione lato server.
        void logoutRequest().catch(() => undefined);
        navigate('/admin/login');
    }, [dispatch, navigate]);

    /**
     * L'effetto del socket non dipende dall'evento selezionato: il callback legge
     * l'evento corrente da questo ref, così il socket resta aperto una sola volta.
     * Un `setReload` (dopo una notifica o la chiusura di una richiesta) o il cambio
     * evento non lo chiudono e riaprono.
     */
    const eventIdRef = useRef<number | ''>(eventId);
    useEffect(() => {
        eventIdRef.current = eventId;
    }, [eventId]);

    // M11: un solo effetto carica la lista e imposta l'auto-refresh da 30 s.
    // Dipendenze stabili (`eventId`, `logged`): al cambio di evento l'effetto viene
    // rieseguito e il cleanup annulla con `AbortController` la richiesta precedente,
    // così ne parte una sola (niente doppie chiamate né scritture obsolete).
    // Il toggle di `reload` (dopo la chiusura di una richiesta) rilegge senza
    // creare un secondo effetto.
    useEffect(() => {
        if (!admin.logged || eventId === '') return;
        // Catturato in una costante: la chiusura `load` lo vede ristretto (number).
        const currentEventId = Number(eventId);
        let active = true;
        const controller = new AbortController();

        const load = async () => {
            setLoading(true);
            try {
                const list = await getEventRequests(currentEventId, controller.signal);
                if (active) setRequests(list);
            } catch (err: unknown) {
                if (!active) return;
                const status = (err as { response?: { status?: number } }).response?.status;
                if (status === 401) handleUnauthorized();
                else setRequests([]);
            } finally {
                if (active) setLoading(false);
            }
        };

        void load();
        // P9: il polling resta attivo anche con il socket: una notifica persa viene
        // comunque recuperata entro 30 s (la lista si rilegge sempre dal REST).
        const interval = setInterval(load, 30000);

        return () => {
            active = false;
            clearInterval(interval);
            controller.abort();
        };
    }, [admin.logged, eventId, reload, handleUnauthorized]);

    // B7: quando un ospite crea una richiesta il backend notifica gli admin. La
    // notifica contiene solo l'id dell'evento: se è quello selezionato si rilegge
    // la lista dal REST (la fonte resta il database). Effetto separato da quello di
    // caricamento, con dipendenza solo da `admin.logged`: così un `setReload` o il
    // cambio evento non chiudono e riaprono il socket. Con il flag spento
    // `connectAdminNotifications` non apre nulla e restituisce un no-op.
    useEffect(() => {
        if (!admin.logged) return;
        return connectAdminNotifications((notifiedEventId) => {
            if (notifiedEventId === eventIdRef.current) setReload((r) => !r);
        });
    }, [admin.logged]);

    const handleClose = async (id: number) => {
        if (!admin.logged) return;
        try {
            await updateRequest(id);
            setReload((r) => !r);
        } catch (err: unknown) {
            const status = (err as { response?: { status?: number } }).response?.status;
            if (status === 401) handleUnauthorized();
        }
    };

    return (
        <div className="flex flex-col gap-4">
            <Paper elevation={0} className="border border-white/10 bg-black/40 p-4">
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Richieste
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Seleziona un evento per vedere le richieste in coda
                </Typography>
                <div className="mt-4 max-w-md">
                    <EventSelector label="Evento" value={eventId} host={admin.host_id} onChange={setEventId} />
                </div>
            </Paper>

            <Paper elevation={0} className="border border-white/10 bg-black/40 p-4">
                {loading ? <LoadingScreen /> : <RequestList items={requests} onClose={handleClose} />}
            </Paper>
        </div>
    );
}
